import { DrawerDetalleObra } from '@/components/dominio/DrawerDetalleObra';
import { TreemapCategorias } from '@/components/dominio/TreemapCategorias';
import { TablaExpandible } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAreaCosteoResumen } from '@/hooks/useAreaCosteo';
import type { ResumenAreaCosteo } from '@adelante/shared';
import { formatearCRC } from '@adelante/shared';
import { CaretDown, CaretRight, CircleNotch, Info } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';

/**
 * Vista Análisis Área Costeo con árbol PowerBI-style de 7 niveles.
 *
 * Jerarquía: Área → Bloque → Sub-bloque → Proyecto → Tarea → Categoría → Item
 *
 * Construcción dinámica: cada fila del recordset SQL es el nivel más
 * granular (item). El frontend agrupa recursivamente para producir el árbol.
 */

interface NodoArbol {
  key: string; // path único de este nodo
  label: string;
  totalCostoLcy: number;
  totalQuantity: number;
  hijos: NodoArbol[]; // [] si es leaf
  nivel: number; // 0=raíz visible (Área), 6=item
}

// 7 niveles de la jerarquía, mapeados a campos de ResumenAreaCosteo.
// IMPORTANTE — nombres de campos backend vs labels de negocio:
//   bloque (BC "VB")    = "Proyecto" en lenguaje Adelante
//   subBloque (BC "1")  = "Bloque"   en lenguaje Adelante
//   worksNo (BC "VB-1.01") = "Obra"  en lenguaje Adelante
const NIVELES: Array<{ field: keyof ResumenAreaCosteo; etiqueta: string }> = [
  { field: 'areaCosteo', etiqueta: 'Área de Costeo' },
  { field: 'bloque', etiqueta: 'Proyecto' },
  { field: 'subBloque', etiqueta: 'Bloque' },
  { field: 'worksNo', etiqueta: 'Obra' },
  { field: 'taskNo', etiqueta: 'Partida' },
  { field: 'itemCategoryCode', etiqueta: 'Categoría' },
  { field: 'descripcionItem', etiqueta: 'Item' },
];

/**
 * Construye el árbol jerárquico desde el recordset plano.
 * Cada fila representa la combinación granular; agrupamos por cada nivel.
 */
function construirArbol(filas: ResumenAreaCosteo[]): NodoArbol[] {
  function recurse(
    filasNivel: ResumenAreaCosteo[],
    nivel: number,
    pathParent: string,
  ): NodoArbol[] {
    const nivelDef = NIVELES[nivel];
    if (!nivelDef) return [];

    const field = nivelDef.field;
    const grupos = new Map<string, ResumenAreaCosteo[]>();
    for (const fila of filasNivel) {
      const raw = fila[field];
      const valor = raw == null ? '(—)' : String(raw);
      if (!grupos.has(valor)) grupos.set(valor, []);
      grupos.get(valor)!.push(fila);
    }

    const nodos: NodoArbol[] = [];
    for (const [valor, filasGrupo] of grupos.entries()) {
      const key = `${pathParent}/${valor}`;
      const hijos = recurse(filasGrupo, nivel + 1, key);
      const primera = filasGrupo[0]!;
      const enriquecerLabel = (): string => {
        // Para nivel "Proyecto" añadimos display name si existe y es distinto
        if (field === 'worksNo') {
          const display = primera.proyectoDisplay;
          return display && display !== valor ? `${valor} — ${display}` : valor;
        }
        // Para nivel "Tarea" añadimos descripción
        if (field === 'taskNo') {
          const display = primera.tareaDisplay;
          return display && display !== valor ? `${valor} — ${display}` : valor;
        }
        return valor;
      };
      nodos.push({
        key,
        label: enriquecerLabel(),
        totalCostoLcy: filasGrupo.reduce((acc, f) => acc + f.totalCostoLcy, 0),
        totalQuantity: filasGrupo.reduce((acc, f) => acc + f.totalQuantity, 0),
        hijos,
        nivel,
      });
    }
    // Ordenar nodos: por costo descendente dentro de cada nivel
    nodos.sort((a, b) => b.totalCostoLcy - a.totalCostoLcy);
    return nodos;
  }
  return recurse(filas, 0, '');
}

export function AreaCosteoPantalla() {
  const [obraDetalle, setObraDetalle] = useState<string | null>(null);
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Análisis Área Costeo</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Árbol jerárquico de 7 niveles: Área de Costeo → Proyecto → Bloque → Obra → Partida →
          Categoría → Item · más gasto por categoría. Tip: click en el ícono{' '}
          <Info className="inline h-3 w-3" weight="bold" /> al lado de una Obra para abrir el
          detalle.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[3fr_2fr]">
        <ArbolCard onVerDetalle={setObraDetalle} />
        <TreemapCategorias titulo="Gasto por Categoría" />
      </div>

      <DrawerDetalleObra worksNo={obraDetalle} onClose={() => setObraDetalle(null)} />
    </div>
  );
}

function ArbolCard({ onVerDetalle }: { onVerDetalle: (worksNo: string) => void }) {
  const { data, isLoading, error } = useAreaCosteoResumen();
  // Inicialmente expandimos solo el primer nivel
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());

  const arbol = useMemo(() => construirArbol(data?.data ?? []), [data]);

  const toggle = (key: string) => {
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandirTodos = () => {
    const todos = new Set<string>();
    const visitar = (nodos: NodoArbol[]) => {
      for (const n of nodos) {
        if (n.hijos.length > 0) {
          todos.add(n.key);
          visitar(n.hijos);
        }
      }
    };
    visitar(arbol);
    setExpandidas(todos);
  };

  const colapsarTodos = () => setExpandidas(new Set());

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 pt-6 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" />
          Cargando jerarquía…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <p className="text-destructive">Error: {error.message}</p>
        </CardContent>
      </Card>
    );
  }

  const acciones = (
    <div className="flex items-center gap-2 text-xs mr-2">
      <button type="button" onClick={expandirTodos} className="text-blue-600 hover:underline">
        Expandir todo
      </button>
      <span className="text-zinc-300">|</span>
      <button type="button" onClick={colapsarTodos} className="text-blue-600 hover:underline">
        Colapsar
      </button>
    </div>
  );

  return (
    <TablaExpandible titulo={`Jerarquía Área Costeo · ${arbol.length} áreas`} acciones={acciones}>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">
            Jerarquía Área Costeo · {arbol.length} áreas
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Jerarquía</TableHead>
                <TableHead className="text-right text-xs w-36">Costo</TableHead>
                <TableHead className="text-right text-xs w-24">Cant</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {arbol.map((nodo) => (
                <FilaArbol
                  key={nodo.key}
                  nodo={nodo}
                  expandidas={expandidas}
                  onToggle={toggle}
                  onVerDetalle={onVerDetalle}
                />
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </TablaExpandible>
  );
}

function FilaArbol({
  nodo,
  expandidas,
  onToggle,
  onVerDetalle,
}: {
  nodo: NodoArbol;
  expandidas: Set<string>;
  onToggle: (k: string) => void;
  onVerDetalle: (worksNo: string) => void;
}) {
  const tieneHijos = nodo.hijos.length > 0;
  const abierto = expandidas.has(nodo.key);
  const indent = nodo.nivel * 16;
  const esObra = nodo.nivel === 3; // jerarquía: 0=Área, 1=Proyecto, 2=Bloque, 3=Obra

  // Color de fondo por nivel para visual clarity
  const bgPorNivel = [
    'bg-zinc-100 font-semibold', // nivel 0 = Área
    'bg-zinc-50 font-medium', // nivel 1 = Bloque
    'bg-white font-medium', // nivel 2 = Sub-bloque
    'bg-white', // nivel 3 = Proyecto
    'bg-white text-muted-foreground', // nivel 4 = Tarea
    'bg-white text-muted-foreground', // nivel 5 = Categoría
    'bg-white text-muted-foreground', // nivel 6 = Item
  ];

  return (
    <>
      <TableRow
        className={`${bgPorNivel[nodo.nivel] ?? ''} ${tieneHijos ? 'cursor-pointer hover:bg-zinc-200' : ''}`}
        onClick={tieneHijos ? () => onToggle(nodo.key) : undefined}
      >
        <TableCell className="text-xs">
          <div className="flex items-center gap-1" style={{ paddingLeft: `${indent}px` }}>
            {tieneHijos ? (
              abierto ? (
                <CaretDown className="h-3 w-3 shrink-0" weight="bold" />
              ) : (
                <CaretRight className="h-3 w-3 shrink-0" weight="bold" />
              )
            ) : (
              <span className="w-3 inline-block" />
            )}
            <span className="truncate max-w-[400px]">{nodo.label}</span>
            {esObra && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const worksNo = nodo.label.split(' — ')[0]!.trim();
                  onVerDetalle(worksNo);
                }}
                title="Ver detalle de la obra"
                className="ml-1 inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                <Info className="h-3.5 w-3.5" weight="bold" />
              </button>
            )}
          </div>
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">
          {formatearCRC(nodo.totalCostoLcy)}
        </TableCell>
        <TableCell className="text-right text-xs tabular-nums">
          {nodo.totalQuantity.toLocaleString('es-CR', { maximumFractionDigits: 1 })}
        </TableCell>
      </TableRow>
      {abierto &&
        nodo.hijos.map((hijo) => (
          <FilaArbol
            key={hijo.key}
            nodo={hijo}
            expandidas={expandidas}
            onToggle={onToggle}
            onVerDetalle={onVerDetalle}
          />
        ))}
    </>
  );
}
